import React, { Fragment, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  ChecklistBasicItem,
  EnumRoomOperationalStatus,
  JsonServiceResolveOrderWithActionsData,
} from '@quality24/inpatient-typings';
import {
  Button,
  Checkbox,
  Modal,
  Text,
  Subtitle,
  ListGroup,
} from '@quality24/design-system';

import styles from './Checklist.module.scss';

export interface Props {
  id: string;
  title?: string;
  description?: string;
  checkLabel?: string;
  items: ChecklistBasicItem[];
  action: JsonServiceResolveOrderWithActionsData;
  hasChoice?: boolean;
  onChange: (items: ChecklistBasicItem[]) => void;
  onHide: () => void;
  onNext: (optionSelected?: string) => void;
}

export const Checklist: React.FC<Props> = ({
  id,
  title,
  description,
  checkLabel,
  items,
  action,
  hasChoice,
  onChange,
  onHide,
  onNext,
}) => {
  const [state, setState] = useState<ChecklistBasicItem[]>(items);
  const [optionSelected, setOptionSelected] = useState<string | undefined>();
  const [choiceModal, setChoiceModal] = useState<boolean>(false);

  /**
   * We are doing the possible to avoid re-renderings between Checklist and Checkbox
   *
   * As anonymous functions have different references on every render
   * we avoid it by passing the memoized onItemClick directly to Checkbox
   * https://alexsidorenko.com/blog/react-list-rerender/
   */
  const onItemClick = useCallback(
    (_id: string) => {
      return setState((prev) => {
        const updated = prev.map((item) => {
          if (item.id === _id) {
            return {
              ...item,
              checked: !item.checked,
            };
          }
          return item;
        });
        onChange?.(updated);
        return updated;
      });
    },
    [onChange],
  );

  const itemsMap = useMemo(() => {
    const othersGroupName = state.some((item) => item.config?.group)
      ? 'Outros itens'
      : 'Todos os itens';

    return Object.entries(
      state.reduce<Record<string, ChecklistBasicItem[]>>((acc, curr) => {
        const group =
          (curr.config?.group as string | undefined) || othersGroupName;

        acc[group] = Array.isArray(acc[group]) ? [...acc[group], curr] : [curr];

        return acc;
      }, {}),
    );
  }, [state]);

  if (hasChoice && choiceModal) {
    const choices = state
      ?.flatMap(
        (checklist) =>
          checklist.checked &&
          checklist?.actions?.map(
            (internalAction) =>
              internalAction.action === 'update-room-op-status' &&
              internalAction.data.newOperationalStatus,
          ),
      )
      .filter((result) => !!result) as EnumRoomOperationalStatus[];

    const options: Array<string> = [];
    if (choices?.includes('WAITING_CLEANING')) options.push('Higienização');
    if (choices?.includes('IN_MAINTENANCE')) options.push('Manutenção');

    const formattedItems = [...options, 'Liberar Leito'];

    return (
      <Modal
        id={id}
        show
        className={styles.modal}
        onHide={onHide}
        title="Alterar Status do Leito"
        closeButton
      >
        <div className="col-12 p-0 mt-0">
          <Text size="md" weight="regular" color="blackLight">
            Por favor, selecione o status que gostaria de colocar neste leito:
          </Text>
          <ListGroup
            className={styles.listGroupChoice}
            value={
              optionSelected
                ? optionSelected.replaceAll(' ', '').toLowerCase()
                : undefined
            }
            onChange={(pressedValues) => {
              setOptionSelected(pressedValues);
            }}
          >
            {formattedItems.map((choice) => {
              const value = choice.replaceAll(' ', '').toLowerCase();
              return (
                <ListGroup.Item
                  value={value}
                  key={value}
                  className={styles.listGroupChoiceItem}
                >
                  {choice && (
                    <Text
                      as="span"
                      size="sm2"
                      weight="semiBold"
                      color="gray400"
                    >
                      {choice}
                    </Text>
                  )}
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </div>
        <div className="col-12 d-flex flex-row flex-justify-end gap-2">
          <Button
            key={`${action.id}--cancel`}
            size="lg"
            variant="outline"
            onClick={() => onHide()}
          >
            Cancelar
          </Button>
          <Button
            size="lg"
            disabled={!optionSelected}
            onClick={() => {
              onNext(optionSelected);
              onHide();
            }}
          >
            <Text color="grayLight1" weight="bold">
              Finalizar Solicitação
            </Text>
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      id="order-checklist"
      show
      className={styles.modal}
      onHide={onHide}
      title={title}
      closeButton
    >
      <Text weight="regular" size="md" color="blackLight">
        {description || 'Selecione os itens que possuem não conformidade'}
      </Text>
      <div className={classNames(styles.container, 'col-12')}>
        <div className={styles.subtitleContainer}>
          <Subtitle size="sm1" weight="semiBold" color="gray300">
            {checkLabel || 'Não Conformidade'}
          </Subtitle>
        </div>
        {itemsMap.map(([group, checklistItems], index) => (
          <Fragment key={group}>
            <Text
              size="sm1"
              weight="semiBold"
              color="gray300"
              className={styles.checklistItemsTitle}
              style={{
                marginTop: index > 0 ? 16 : 8,
              }}
            >
              {group}
            </Text>
            <div className={styles.checklistRow}>
              {checklistItems.map((i) => (
                <div key={i.id}>
                  <div className={styles.checklistPressable}>
                    <Button variant="link" onClick={() => onItemClick(i.id)}>
                      <Text size="sm2" color="blackLight">
                        {i.title || i.id || ''}
                      </Text>
                    </Button>
                    <Checkbox
                      name={i.id}
                      label=""
                      className={styles.checkbox}
                      value={i.id}
                      checked={
                        state.find((item) => item.id === i.id)?.checked ||
                        i.checked
                      }
                      onChange={() => onItemClick(i.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Fragment>
        ))}
      </div>
      <div className="col-12 d-flex flex-row flex-justify-end gap-2">
        <Button
          key={`${action.id}--cancel`}
          size="lg"
          variant="outline"
          onClick={() => onHide()}
        >
          Cancelar
        </Button>
        <Button
          key={action.id}
          size="lg"
          onClick={() => {
            if (hasChoice) {
              setChoiceModal(true);
              return;
            }

            onNext();
            onHide();
          }}
        >
          {hasChoice
            ? 'Avançar'
            : action?.data?.label || action?.data?.title || 'Confirmar'}
        </Button>
      </div>
    </Modal>
  );
};

export default Checklist;
